// DatasetBuilderPage — `/datasets/new` (BACKLOG.md storage uplift P4).
//
// Workspace-scoped dataset assembly: pick envelope facets → live count via
// `:count` → preview rows via `:query` → export a manifest via `:export`.
// Tier badge surfaces whether the request was served by T1 / T2 / T3 so
// the operator understands cost source.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { datasetsCount, type DatasetCountResult } from '../api';
import { useToast } from '../components/Toast';

interface QueryRow {
  event_id: string;
  source_id: string;
  received_at: string;
  derivation_depth: number;
  origin: { kind: string; id: string };
  payload: Record<string, unknown>;
}

interface QueryResponse {
  tier_used: 't1';
  tier_reason: string;
  rows: QueryRow[];
  row_count: number;
  capped: boolean;
  bytes_scanned: number;
  cost_estimate_usd: number;
  dataset_hash: string;
}

interface ExportResponse {
  dataset_hash: string;
  mode: 'manifest';
  manifest_url: string | null;
  manifest_path: string;
  manifest_object_count: number;
  ttl_seconds: number;
  tier_used: 't1';
  capped: boolean;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontFamily: 'var(--mono)',
  fontSize: 12.5,
  border: '1px solid var(--line)',
  borderRadius: 4,
  background: 'var(--bg)',
  color: 'var(--ink)',
  width: '100%',
  boxSizing: 'border-box',
};

export default function DatasetBuilderPage(): React.ReactElement {
  const toast = useToast();

  const [sourceIdsInput, setSourceIdsInput] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [depthMax, setDepthMax] = useState<'any' | '0' | '1' | '2' | '3' | '4' | '5'>('any');
  const [originKind, setOriginKind] = useState<'any' | 'input' | 'trigger'>('any');
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');

  const [count, setCount] = useState<DatasetCountResult | null>(null);
  const [countErr, setCountErr] = useState<string | null>(null);
  const [counting, setCounting] = useState(false);

  const [previewRows, setPreviewRows] = useState<QueryRow[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResponse | null>(null);

  const filter = useMemo(() => {
    const f: Record<string, unknown> = {};
    const sourceIds = sourceIdsInput
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (sourceIds.length > 0) f.source_ids = sourceIds;
    if (from) f.received_from = new Date(from).toISOString();
    if (to) f.received_to = new Date(to).toISOString();
    if (depthMax !== 'any') f.derivation_depth_max = Number(depthMax);
    if (originKind !== 'any') f.origin_kinds = [originKind];
    if (tagKey && tagValue) f.tags = { [tagKey]: [tagValue] };
    return f;
  }, [sourceIdsInput, from, to, depthMax, originKind, tagKey, tagValue]);

  // Filter must narrow by at least source_ids OR a date range, otherwise
  // the backend rejects with `filter_too_broad` (an empty filter would
  // walk the entire bucket and time out at the Cloud Run ceiling).
  const filterIsNarrowed = (
    Array.isArray((filter as Record<string, unknown>).source_ids) && ((filter as { source_ids: unknown[] }).source_ids.length > 0)
  ) || typeof (filter as Record<string, unknown>).received_from === 'string'
    || typeof (filter as Record<string, unknown>).received_to === 'string'
    || (Array.isArray((filter as Record<string, unknown>).parent_event_ids) && ((filter as { parent_event_ids: unknown[] }).parent_event_ids.length > 0));

  // Live count (debounced). Skip the API call when the filter is empty —
  // we'd just get a 422 — and surface a friendly hint instead.
  useEffect(() => {
    if (!filterIsNarrowed) {
      setCount(null);
      setCounting(false);
      setCountErr(null);
      return;
    }
    setCounting(true); setCountErr(null);
    const handle = setTimeout(() => {
      datasetsCount(filter)
        .then(r => setCount(r))
        .catch(e => setCountErr(String(e)))
        .finally(() => setCounting(false));
    }, 350);
    return () => { clearTimeout(handle); setCounting(false); };
  }, [filter, filterIsNarrowed]);

  async function preview(): Promise<void> {
    setPreviewing(true); setPreviewErr(null);
    try {
      const r = await fetch('/api/v1/datasets:query', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter, limit: 25 }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
        throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
      }
      const body = await r.json() as QueryResponse;
      setPreviewRows(body.rows);
    } catch (e) {
      setPreviewErr(String(e));
    } finally {
      setPreviewing(false);
    }
  }

  async function exportManifest(): Promise<void> {
    setExporting(true);
    try {
      const r = await fetch('/api/v1/datasets:export', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
        throw new Error(e.error?.detail ?? e.error?.code ?? `HTTP ${r.status}`);
      }
      const body = await r.json() as ExportResponse;
      setExportResult(body);
      toast.show(`Manifest exported · ${body.manifest_object_count.toLocaleString()} objects · ${body.dataset_hash}`, { kind: 'success' });
    } catch (e) {
      toast.show(`Export failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setExporting(false);
    }
  }

  const facetCount = (sourceIdsInput.trim() ? 1 : 0)
    + (from ? 1 : 0)
    + (to ? 1 : 0)
    + (depthMax !== 'any' ? 1 : 0)
    + (originKind !== 'any' ? 1 : 0)
    + (tagKey && tagValue ? 1 : 0);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px', fontFamily: 'var(--sans)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Dataset Builder</h1>
        <Link to="/sources" style={{ fontSize: 12, color: 'var(--ink-3)' }}>← Sources</Link>
      </div>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 24 }}>
        Assemble a workspace-scoped dataset from Object Context envelope facets. Live count via <code style={{ fontFamily: 'var(--mono)' }}>:count</code> (T1, no payload bytes scanned). Export a manifest of signed URLs that any downstream consumer can parallelize against.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <Field label="source_ids (comma- or space-separated, blank = all)">
          <input value={sourceIdsInput} onChange={e => setSourceIdsInput(e.target.value)} placeholder="src_..., src_..." style={inputStyle} />
        </Field>
        <Field label="tag (key + value)">
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={tagKey} onChange={e => setTagKey(e.target.value)} placeholder="env" style={{ ...inputStyle, flex: 1 }} />
            <input value={tagValue} onChange={e => setTagValue(e.target.value)} placeholder="prod" style={{ ...inputStyle, flex: 1 }} />
          </div>
        </Field>
        <Field label="received_from (UTC)">
          <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="received_to (UTC, exclusive)">
          <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="derivation_depth ≤">
          <select value={depthMax} onChange={e => setDepthMax(e.target.value as typeof depthMax)} style={inputStyle}>
            <option value="any">any</option>
            <option value="0">0 (inputs only)</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </Field>
        <Field label="origin">
          <select value={originKind} onChange={e => setOriginKind(e.target.value as typeof originKind)} style={inputStyle}>
            <option value="any">any</option>
            <option value="input">input</option>
            <option value="trigger">trigger derivative</option>
          </select>
        </Field>
      </div>

      {/* COUNT + DISTRIBUTION */}
      <div style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Match count</div>
          <span style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 6px', borderRadius: 3 }}>tier · t1</span>
        </div>
        {!filterIsNarrowed && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', padding: 8, background: 'var(--bg-sunken)', border: '1px dashed var(--line)', borderRadius: 6 }}>
            Pick at least one source, or set a date range, before counting.
            An empty filter would walk every event ever ingested and time
            out — by design.
          </div>
        )}
        {filterIsNarrowed && counting && <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>counting…</div>}
        {filterIsNarrowed && countErr && <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>error: {countErr}</div>}
        {filterIsNarrowed && count && !counting && (
          <>
            <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--ink)' }}>{count.count_approximate.toLocaleString()}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
              {count.count_capped ? '⚠ capped at 100k — narrow filter for accurate count' : 'approximate · batched blobs counted as 1 entry until P3 backfill'}
              {count.dataset_hash && <> · <code>{count.dataset_hash}</code></>}
            </div>
            {count.distribution_by_source.length > 1 && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', cursor: 'pointer' }}>by source · {count.distribution_by_source.length}</summary>
                <ul style={{ margin: '6px 0 0', padding: '0 0 0 20px', fontSize: 12 }}>
                  {count.distribution_by_source.slice(0, 10).map(d => (
                    <li key={d.source_id}><code style={{ fontFamily: 'var(--mono)' }}>{d.source_id}</code> · {d.count.toLocaleString()}</li>
                  ))}
                </ul>
              </details>
            )}
            {count.distribution_by_date.length > 1 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', cursor: 'pointer' }}>by date · {count.distribution_by_date.length}</summary>
                <ul style={{ margin: '6px 0 0', padding: '0 0 0 20px', fontSize: 12 }}>
                  {count.distribution_by_date.slice(0, 14).map(d => (
                    <li key={d.date}><code style={{ fontFamily: 'var(--mono)' }}>{d.date}</code> · {d.count.toLocaleString()}</li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </div>

      {/* ACTIONS */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button className="btn ghost" onClick={() => { void preview(); }} disabled={previewing || facetCount === 0}>{previewing ? 'Previewing…' : 'Preview 25 rows'}</button>
        <button className="btn primary" onClick={() => { void exportManifest(); }} disabled={exporting || !count || count.count_approximate === 0}>{exporting ? 'Exporting…' : 'Export manifest'}</button>
        <div style={{ flex: 1 }} />
        {facetCount === 0 && <span style={{ fontSize: 11.5, color: 'var(--ink-3)', alignSelf: 'center' }}>tip — narrow filters before exporting; empty filter = the whole workspace</span>}
      </div>

      {/* PREVIEW */}
      {previewErr && <div style={{ padding: 10, background: 'oklch(0.95 0.04 25)', border: '1px solid var(--danger)', borderRadius: 6, color: 'var(--danger)', fontSize: 12, marginBottom: 18 }}>preview error: {previewErr}</div>}
      {previewRows && previewRows.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Preview · {previewRows.length} row{previewRows.length === 1 ? '' : 's'}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink-3)', fontWeight: 500 }}>received_at</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink-3)', fontWeight: 500 }}>source_id</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink-3)', fontWeight: 500 }}>origin</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink-3)', fontWeight: 500 }}>depth</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink-3)', fontWeight: 500 }}>payload</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map(r => (
                  <tr key={r.event_id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{r.received_at}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)' }}>{r.source_id}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)' }}>{r.origin.kind}/{r.origin.id}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)' }}>{r.derivation_depth}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(r.payload)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EXPORT RESULT */}
      {exportResult && (
        <div style={{ padding: 16, border: '1px solid var(--accent-line)', background: 'var(--accent-soft)', borderRadius: 8, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Manifest ready</div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            <strong>{exportResult.manifest_object_count.toLocaleString()}</strong> objects · TTL {Math.round(exportResult.ttl_seconds / 3600)}h · <code style={{ fontFamily: 'var(--mono)' }}>{exportResult.dataset_hash}</code>
          </div>
          <div style={{ fontSize: 12, marginTop: 8, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
            <span style={{ color: 'var(--ink-3)' }}>path </span>
            <span>{exportResult.manifest_path}</span>
          </div>
          {exportResult.manifest_url && (
            <div style={{ marginTop: 8 }}>
              <a href={exportResult.manifest_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'underline' }}>Download manifest (signed URL)</a>
            </div>
          )}
          {exportResult.capped && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'oklch(0.55 0.15 75)' }}>⚠ Filter exceeded the 100k object cap — narrow further or page via received_to.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}>{label}</span>
      {children}
    </label>
  );
}
